import { forwardRef, ReactNode, useId } from 'react';

import { ChangeHandler, get, useFormState } from 'react-hook-form';
import Label from './Label';
import Error, { type ErrorMessage } from './Error';
import clsx from 'clsx';
import { fieldWidths } from './Form';

export type Option = Readonly<{
  label: ReactNode;
  value: string;
  description?: string;
}>;

export interface SelectorInputFieldProps {
  label?: string;
  description?: string;
  name?: string;
  id?: string;
  disabled?: boolean;
  additionalContainerClasses?: string;
  options: Readonly<Option[]>;
  onChange?: ChangeHandler;
  onBlur?: ChangeHandler;
  error?: ErrorMessage;
  value?: string;
  width?: keyof typeof fieldWidths;
}

export const SelectorInputField = forwardRef<HTMLSelectElement, SelectorInputFieldProps>(
  function InternalSelectorInputField(
    {
      label,
      description,
      name,
      id,
      additionalContainerClasses,
      onChange,
      onBlur,
      error,
      options,
      value,
      disabled,
      width = 'full',
    },
    ref
  ) {
    const formState = useFormState();
    const formError = get(formState.errors, name);
    const resolvedError = error ?? formError;

    const defaultId = useId();
    id = id ?? defaultId;

    return (
      <div
        className={clsx(
          'relative gap-2 flex flex-col w-full',
          additionalContainerClasses,
          fieldWidths[width]
        )}
        id={id}
      >
        {label ? (
          <Label htmlFor={`${id}::select`} description={description}>
            {label}
          </Label>
        ) : null}
        <div className="relative flex w-full">
          <select
            onChange={onChange}
            onBlur={onBlur}
            ref={ref}
            name={name}
            id={`${id}::select`}
            disabled={disabled}
            defaultValue={value}
            className="w-full px-2 py-2 border rounded-md appearance-none disabled:bg-transparent dark:text-blue-100 text-grey-1000 bg-grey-100 border-grey-500 placeholder:text-grey-500 dark:placeholder:text-grey-400 dark:bg-blue-900 dark:border-grey-600 focus:border-blue-500 dark:focus:border-blue-300 focus:outline-none"
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span className="absolute  top-1/4 -translate-y-[1px] flex items-center h-full pointer-events-none right-[9px] dark:text-blue-100 text-grey-1000 material-symbols-outlined">
            expand_more
          </span>
        </div>

        <Error error={resolvedError} />
      </div>
    );
  }
);
